"use client";

import styles from "@/styles/modules/components/meetings-and-events/moduleRoomsRoom.module.scss";
import { MeetingsAndEventsRoom } from "@/types/wordpress";
import Image from "next/image";
import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import FreeText from "../FreeText";
import Link from "next/link";
import { BookMenuContext } from "@/context/bookMenuContext";
import Gallery from "../Gallery";
import { LanguageContext } from "@/context/languageContext";
import { translate } from "@/utils/translate";

interface ModuleRoomsRoomProps {
  room: MeetingsAndEventsRoom;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<number | null>>;
  id: number;
}

export default function ModuleRoomsRoom({
  opened,
  room,
  setOpened,
  id,
}: ModuleRoomsRoomProps) {
  const { language } = useContext(LanguageContext);
  const { openBookMenu, setCurrentBookMenu } = useContext(BookMenuContext);
  const roomRef = useRef<HTMLElement>(null);
  const [firstTime, setFirstTime] = useState(true);

  const handleInquiry = () => {
    setCurrentBookMenu(2);
    openBookMenu();
  };

  const handleHeaderClick = () => {
    setOpened(opened ? null : id);
  };

  useEffect(() => {
    if (opened && !firstTime) {
      roomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    setFirstTime(false);
    //dont add firstTime, ignore warning
  }, [opened]);

  return (
    <article
      className={`${styles.room} ${opened ? styles.opened : ""}`}
      ref={roomRef}
    >
      <header onClick={handleHeaderClick}>
        <h2>{language === "en" ? room.title : room.translations[0].title}</h2>
        <span />
      </header>
      <div className={styles.content}>
        {language === "en"
          ? room.contentFields.gallery && (
            <Gallery gallery={room.contentFields.gallery} />
          )
          : room.translations[0].contentFields.gallery && (
            <Gallery gallery={room.translations[0].contentFields.gallery} />
          )}
        <div className={styles.info}>
          {language === "en"
            ? room.contentFields.body && (
              <div className={styles.text}>
                <FreeText content={room.contentFields.body} />
              </div>
            )
            : room.translations[0].contentFields.body && (
              <div className={styles.text}>
                <FreeText content={room.translations[0].contentFields.body} />
              </div>
            )}
          {language === "en"
            ? (room.rentRooms.checkboxesCapacity ||
              room.rentRooms.checkboxesScreen ||
              room.rentRooms.checkboxesSize ||
              room.rentRooms.checkboxesWifi) && (
              <div className={styles.checkboxes}>
                {room.rentRooms.checkboxesSize && (
                  <div className={styles.box}>
                    <Image
                      alt="Square icon"
                      src="/icons/checkbox_square.svg"
                      width={27}
                      height={27}
                    />
                    <span>{room.rentRooms.checkboxesSize}</span>
                  </div>
                )}
                {room.rentRooms.checkboxesCapacity && (
                  <div className={styles.box}>
                    <Image
                      alt="Person icon"
                      src="/icons/checkbox_person.svg"
                      width={22}
                      height={26}
                    />
                    <span>{room.rentRooms.checkboxesCapacity}</span>
                  </div>
                )}
                {room.rentRooms.checkboxesScreen && (
                  <div className={styles.box}>
                    <Image
                      alt="Screen icon"
                      src="/icons/checkbox_tv.svg"
                      width={31}
                      height={25}
                    />
                    <span>{room.rentRooms.checkboxesScreen}</span>
                  </div>
                )}
                {room.rentRooms.checkboxesWifi && (
                  <div className={styles.box}>
                    <Image
                      alt="Wifi icon"
                      src="/icons/checkbox_wifi.svg"
                      width={26}
                      height={22}
                    />
                    <span>{room.rentRooms.checkboxesWifi}</span>
                  </div>
                )}
              </div>
            )
            : (room.translations[0].rentRooms.checkboxesCapacity ||
              room.translations[0].rentRooms.checkboxesScreen ||
              room.translations[0].rentRooms.checkboxesSize ||
              room.translations[0].rentRooms.checkboxesWifi) && (
              <div className={styles.checkboxes}>
                {room.translations[0].rentRooms.checkboxesSize && (
                  <div className={styles.box}>
                    <Image
                      alt="Square icon"
                      src="/icons/checkbox_square.svg"
                      width={27}
                      height={27}
                    />
                    <span>
                      {room.translations[0].rentRooms.checkboxesSize}
                    </span>
                  </div>
                )}
                {room.translations[0].rentRooms.checkboxesCapacity && (
                  <div className={styles.box}>
                    <Image
                      alt="Person icon"
                      src="/icons/checkbox_person.svg"
                      width={22}
                      height={26}
                    />
                    <span>
                      {room.translations[0].rentRooms.checkboxesCapacity}
                    </span>
                  </div>
                )}
                {room.translations[0].rentRooms.checkboxesScreen && (
                  <div className={styles.box}>
                    <Image
                      alt="Screen icon"
                      src="/icons/checkbox_tv.svg"
                      width={31}
                      height={25}
                    />
                    <span>
                      {room.translations[0].rentRooms.checkboxesScreen}
                    </span>
                  </div>
                )}
                {room.translations[0].rentRooms.checkboxesWifi && (
                  <div className={styles.box}>
                    <Image
                      alt="Wifi icon"
                      src="/icons/checkbox_wifi.svg"
                      width={26}
                      height={22}
                    />
                    <span>
                      {room.translations[0].rentRooms.checkboxesWifi}
                    </span>
                  </div>
                )}
              </div>
            )}
          {language === "en"
            ? room.rentRooms.links && (
              <div className={styles.links}>
                {room.rentRooms.links &&
                  room.rentRooms.links.map((link, i) => (
                    <Link
                      key={i}
                      href={link.link.url}
                      target={link.link.target}
                    >
                      {link.link.title}
                    </Link>
                  ))}
              </div>
            )
            : room.translations[0].rentRooms.links && (
              <div className={styles.links}>
                {room.translations[0].rentRooms.links &&
                  room.translations[0].rentRooms.links.map((link, i) => (
                    <Link
                      key={i}
                      href={link.link.url}
                      target={link.link.target}
                    >
                      {link.link.title}
                    </Link>
                  ))}
              </div>
            )}
          <div className={styles.button}>
            {language === "en" ? (
              room.contentFields.button ? (
                <Link
                  href={room.contentFields.button.url}
                  target={room.contentFields.button.target}
                >
                  {room.contentFields.button.title}
                </Link>
              ) : (
                <button onClick={handleInquiry}>
                  {translate["makeAnInquiry"][language]}
                </button>
              )
            ) : room.translations[0].contentFields.button &&
              room.translations[0].contentFields.button.title &&
              room.translations[0].contentFields.button.url ? (
              <Link
                href={room.translations[0].contentFields.button.url}
                target={room.translations[0].contentFields.button.target}
              >
                {room.translations[0].contentFields.button.title}
              </Link>
            ) : (
              <button onClick={handleInquiry}>
                {translate["makeAnInquiry"][language]}
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
